import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import logger from '../utils/logger';
import UndoRedoManager from '../services/UndoRedoManager';

/**
 * Custom hook for tracking changes in schedule entries
 * Extracted from ScheduleDisplay to enable reuse in ClassSchedulerPage
 */
export function useChangeTracking(user, options = {}) {
    const [changeLog, setChangeLog] = useState([]);
    const [serverBaseline, setServerBaseline] = useState([]); // Simple server baseline for comparison
    const [isChangeLogModalOpen, setIsChangeLogModalOpen] = useState(false);
    const { baselineSource, baselineReady, baselineVersion } = options;
    const [currentBaselineVersion, setCurrentBaselineVersion] = useState(null);

    // Track IDs per action type for targeted save operations
    const createdIdsRef = useRef(new Set());
    const modifiedIdsRef = useRef(new Set());
    const deletedIdsRef = useRef(new Set());

    // Undo/Redo manager - persists across change log resets
    const undoRedoManager = useMemo(() => new UndoRedoManager(), []);

    // Helpers to access and reset change ID sets
    const getChangeIdSets = useCallback(() => {
        return {
            createdIds: new Set(createdIdsRef.current),
            modifiedIds: new Set(modifiedIdsRef.current),
            deletedIds: new Set(deletedIdsRef.current)
        };
    }, []);

    const resetChangeIdSets = useCallback(() => {
        createdIdsRef.current = new Set();
        modifiedIdsRef.current = new Set();
        deletedIdsRef.current = new Set();
    }, []);

    // Helper to detect placeholder entries that should be ignored for net change detection
    const isPlaceholderEntry = (entry) => {
        if (!entry) return false;
        const data = typeof entry.toObject === 'function' ? entry.toObject() : entry;
        const name = (data && (data.displayName || data.course || data.title) || '').toString().trim();
        if (name === '__PLACEHOLDER__') return true;
        if (data && data.type === 'placeholder') return true;
        if (data && data.additionalProps && data.additionalProps.isPlaceholder === true) return true;
        return false;
    };
    
    // Startup trace
    useEffect(() => {
        console.debug('[useChangeTracking] mounted', {
            user: user?.username || 'Unknown'
        });
        return () => {
            console.debug('[useChangeTracking] unmounted');
        };
    }, [user?.username]);

    // Log Entry Handler - extracted from ScheduleDisplay
    const addLogEntry = useCallback((action, entryBefore, entryAfter) => {
        // Normalize action aliases
        const normalizedAction = action === 'edit' ? 'modify' : action;
        const timestamp = new Date().toISOString();
        const safeName = (e) => e?.displayName || e?.course || e?.title || 'Unknown';
        const displayNameBefore = safeName(entryBefore);
        const displayNameAfter = safeName(entryAfter) || displayNameBefore;
        let details = `'${displayNameAfter || displayNameBefore}'`;

        if (normalizedAction === 'modify') {
            const oldExceptions = Array.isArray(entryBefore?.exceptions) ? entryBefore.exceptions : [];
            const newExceptions = Array.isArray(entryAfter?.exceptions) ? entryAfter.exceptions : [];

            if (newExceptions.length > oldExceptions.length) {
                // This logic assumes the most recent exception is the new one.
                const newException = newExceptions[newExceptions.length - 1];
                const week = newException.week;
                const changes = newException.changes;
                const changeDescriptions = [];

                if (changes.cancelled) {
                    details += ` cancelled for week ${week}.`;
                } else {
                    if (changes.day || changes.room || changes.timeStart) {
                        const newTime = changes.timeStart && changes.timeEnd ? `${changes.timeStart}-${changes.timeEnd}` : entryAfter.formattedTime;
                        changeDescriptions.push(`moved to ${changes.room || entryAfter.room} on ${changes.day || entryAfter.day} at ${newTime}`);
                    }
                    // Add other field comparisons as needed
                    if (changeDescriptions.length > 0) {
                        details += ` for week ${week} was ${changeDescriptions.join(', ')}.`;
                    } else {
                        details += ` was modified for week ${week}.`;
                    }
                }
            } else if (newExceptions.length < oldExceptions.length) {
                // Exception was removed - find which one
                const oldExceptionsMap = new Map(oldExceptions.map(ex => [`${ex.week}-${ex.year}`, ex]));
                const newExceptionsMap = new Map(newExceptions.map(ex => [`${ex.week}-${ex.year}`, ex]));
                
                const removedExceptions = [];
                oldExceptions.forEach(oldException => {
                    const key = `${oldException.week}-${oldException.year}`;
                    if (!newExceptionsMap.has(key)) {
                        removedExceptions.push(oldException.week);
                    }
                });
                
                if (removedExceptions.length > 0) {
                    details += ` exception removed for week${removedExceptions.length > 1 ? 's' : ''} ${removedExceptions.join(', ')}.`;
                } else {
                    details += ` exception updated.`;
                }
            } else {
                 // Fallback to original detailed modify logic for non-exception modifications
                const changes = [];
                const fields = [
                    { key: 'timeRangeDisplay', label: 'time', getValue: (entry) => entry.timeRangeDisplay ? entry.timeRangeDisplay() : 'N/A' },
                    { key: 'course', label: 'course', getValue: (entry) => entry.course || 'N/A' },
                    {
                        key: 'teachers',
                        label: 'teachers',
                        getValue: (entry) => {
                            // Prefer multi-teacher formatted string when available
                            const formatted = typeof entry?.teachersFormatted === 'string' ? entry.teachersFormatted.trim() : '';
                            if (formatted) return formatted;
                            // Fallback to legacy single teacher string
                            return entry?.teacher || 'N/A';
                        }
                    },
                    { key: 'groups', label: 'groups', getValue: (entry) => Array.isArray(entry.groups) ? entry.groups.map(g => g.name || g).join(', ') : (entry.groups || 'N/A') },
                    { key: 'room', label: 'room', getValue: (entry) => entry.room || 'N/A' },
                    { key: 'day', label: 'day', getValue: (entry) => entry.day || 'N/A' },
                    { key: 'weekSeries', label: 'weekSeries', getValue: (entry) => entry.weekSeries || 'N/A' }
                ];

                fields.forEach(field => {
                    const beforeVal = field.getValue(entryBefore);
                    const afterVal = field.getValue(entryAfter);
                    if (beforeVal !== afterVal) {
                        changes.push(`${field.label}: '${beforeVal}' -> '${afterVal}'`);
                    }
                });
                details += ` modified for all weeks. Changes: ${changes.join(', ') || 'No details changed'}`;
            }
        } else if (normalizedAction === 'move') {
            details += ` moved from ${entryBefore?.room} (${entryBefore?.day} ${entryBefore?.formattedTime}) to ${entryAfter?.room} (${entryAfter?.day} ${entryAfter?.formattedTime}). This applies to all weeks.`;
        } else if (normalizedAction === 'create') {
            details += ` created in ${entryAfter?.room} (${entryAfter?.day} ${entryAfter?.formattedTime}).`;
        } else if (normalizedAction === 'delete') {
            details += ` removed from ${entryBefore?.room} (${entryBefore?.day} ${entryBefore?.formattedTime}).`;
        }

        const logEntry = {
            timestamp,
            user: user?.username || 'Unknown',
            action: normalizedAction,
            itemId: entryAfter?.id || entryBefore?.id || 'N/A',
            details
        };

        setChangeLog(prevLog => [logEntry, ...prevLog]);
        console.debug('[useChangeTracking] addLogEntry', logEntry);

        // Record action in undo/redo manager
        try {
            undoRedoManager.recordAction(normalizedAction, entryBefore, entryAfter, logEntry.itemId);
        } catch (e) {
            logger.warn('[useChangeTracking] Failed to record undo/redo action:', e);
        }

        // Update ID change sets for targeted saves
        try {
            const id = (entryAfter?.id != null ? entryAfter.id : entryBefore?.id);
            const idStr = id != null ? String(id) : null;
            if (!idStr || idStr === 'N/A') return;

            // Treat 'move' as a modification for our purposes
            const effectiveAction = normalizedAction === 'move' ? 'modify' : normalizedAction;

            if (effectiveAction === 'create') {
                // New items go to created; ensure it's not marked deleted/modified
                deletedIdsRef.current.delete(idStr);
                modifiedIdsRef.current.delete(idStr);
                createdIdsRef.current.add(idStr);
            } else if (effectiveAction === 'modify') {
                // If it was newly created in this session, keep it only in created
                if (!createdIdsRef.current.has(idStr)) {
                    modifiedIdsRef.current.add(idStr);
                }
            } else if (effectiveAction === 'delete') {
                // Deletion supersedes create/modify
                createdIdsRef.current.delete(idStr);
                modifiedIdsRef.current.delete(idStr);
                deletedIdsRef.current.add(idStr);
            }
            console.debug('[useChangeTracking] ID sets after update', {
                lastAction: effectiveAction,
                lastId: idStr,
                createdCount: createdIdsRef.current.size,
                modifiedCount: modifiedIdsRef.current.size,
                deletedCount: deletedIdsRef.current.size,
            });
        } catch (e) {
            console.warn('[useChangeTracking] Failed to update change ID sets:', e);
        }
    }, [user?.username]);

    // Calculate net changes - simple comparison between current state and server baseline
    const calculateNetChanges = useCallback((currentScheduleItems) => {
        if (!serverBaseline || !currentScheduleItems) {
            console.log("Skipping net changes calculation: missing data", {
                hasBaseline: !!serverBaseline,
                hasCurrentItems: !!currentScheduleItems
            });
            return;
        }
        
        // Ignore placeholder entries in both baseline and current comparisons
        const filteredBaseline = (serverBaseline || []).filter(entry => !isPlaceholderEntry(entry));
        const filteredCurrent = (currentScheduleItems || []).filter(entry => !isPlaceholderEntry(entry));

        logger.debug("[useChangeTracking] Calculating net changes...", {
            baselineLength: filteredBaseline.length,
            currentLength: filteredCurrent.length
        });
        
        const netChanges = [];
        const baselineItemsMap = new Map(filteredBaseline.map(entry => [entry.id, entry]));
        const currentItemsMap = new Map(filteredCurrent.map(entry => [entry.id, entry]));
        const timestamp = new Date().toISOString();
        const currentUser = user?.username || 'Unknown';

        // Helpers to normalize values for comparison/display
        const normalizeTeacher = (val, entryObj) => {
            // Prefer multi-teacher formatted string when available on the entry
            const formatted = typeof (entryObj && entryObj.teachersFormatted) === 'string' ? entryObj.teachersFormatted.trim() : '';
            if (formatted) return formatted;
            // Fallbacks for legacy shapes
            if (!val) return 'N/A';
            if (typeof val === 'string') return val;
            if (typeof val === 'object') return val.name || 'N/A';
            return String(val);
        };
        const normalizeRoom = (val) => {
            if (!val) return 'N/A';
            if (typeof val === 'string') return val;
            if (typeof val === 'object') return val.name || val.room_code || 'N/A';
            return String(val);
        };
        const normalizeTime = (val) => {
            if (!val || typeof val !== 'string') return 'N/A';
            const parts = val.split(':');
            if (parts.length >= 2) {
                const hh = parts[0].padStart(2, '0');
                const mm = parts[1].padStart(2, '0');
                return `${hh}:${mm}`;
            }
            return val;
        };
        const normalizeGroups = (val) => {
            if (!Array.isArray(val)) return 'N/A';
            return val.map(g => {
                if (typeof g === 'object' && g !== null) return g.name || g.group_code || g.id;
                return g;
            }).join(', ');
        };

        const netChangeDebugSummary = {
            created: [],
            modified: [],
            deleted: [],
            conflictOnly: [],
            fieldDiffCount: 0,
            conflictStateDiffCount: 0,
        };

        filteredBaseline.forEach(baselineEntry => {
            // Get the current entry for the same ID
            const currentEntry = currentItemsMap.get(baselineEntry.id);
            if (currentEntry) {
                // Convert current entry to plain object for comparison
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function' 
                    ? currentEntry.toObject() 
                    : currentEntry;

                const changes = [];
                
                // Compare base properties (normalized for object-based fields)
                const fieldsToCompare = [
                    { key: 'day', label: 'day', getValue: (entry) => entry.day || 'N/A' },
                    { key: 'timeStart', label: 'timeStart', getValue: (entry) => normalizeTime(entry.timeStart) },
                    { key: 'timeEnd', label: 'timeEnd', getValue: (entry) => normalizeTime(entry.timeEnd) },
                    { key: 'course', label: 'course', getValue: (entry) => entry.course || 'N/A' },
                    { key: 'teachers', label: 'teachers', getValue: (entry) => normalizeTeacher(entry.teacher, entry) },
                    { key: 'groups', label: 'groups', getValue: (entry) => normalizeGroups(entry.groups) },
                    { key: 'room', label: 'room', getValue: (entry) => normalizeRoom(entry.room) },
                    { key: 'type', label: 'type', getValue: (entry) => entry.type || 'N/A' },
                    { key: 'weekSeries', label: 'weekSeries', getValue: (entry) => entry.weekSeries || entry.week_series || 'N/A' }
                ];
                
                // Log conflict-related fields for debugging (but don't compare them)
                const baselineHasConflicts = !!baselineEntry.conflictsByWeek;
                const currentHasConflicts = !!currentEntryData.conflictsByWeek;
                if (baselineHasConflicts !== currentHasConflicts) {
                    netChangeDebugSummary.conflictStateDiffCount += 1;
                }
                fieldsToCompare.forEach(field => {
                    const baselineValue = field.getValue(baselineEntry);
                    const currentValue = field.getValue(currentEntryData);
                    if (baselineValue !== currentValue) {
                        netChangeDebugSummary.fieldDiffCount += 1;
                        changes.push(`${field.label}: '${baselineValue}' -> '${currentValue}'`);
                    }
                });

                // Compare exceptions arrays
                const baselineExceptions = Array.isArray(baselineEntry.exceptions) ? baselineEntry.exceptions : [];
                const currentExceptions = Array.isArray(currentEntryData.exceptions) ? currentEntryData.exceptions : [];
                
                // Create maps for easier comparison
                const baselineExceptionsMap = new Map(
                    baselineExceptions.map(ex => [`${ex.week}-${ex.year}`, ex])
                );
                const currentExceptionsMap = new Map(
                    currentExceptions.map(ex => [`${ex.week}-${ex.year}`, ex])
                );

                // Check for new or modified exceptions
                currentExceptions.forEach(currentException => {
                    const key = `${currentException.week}-${currentException.year}`;
                    const baselineException = baselineExceptionsMap.get(key);
                    
                    if (!baselineException) {
                        // New exception - compare against base item values
                        const exceptionChanges = [];
                        const exceptionData = currentException.changes || {};
                        
                        if (exceptionData.cancelled) {
                            changes.push(`cancelled for week ${currentException.week}`);
                        } else {
                            // Compare each field in the exception against the base item
                            if (exceptionData.day && exceptionData.day !== currentEntryData.day) {
                                exceptionChanges.push(`moved from ${currentEntryData.day} to ${exceptionData.day}`);
                            }
                            if (exceptionData.room && exceptionData.room !== currentEntryData.room) {
                                exceptionChanges.push(`moved from ${currentEntryData.room} to ${exceptionData.room}`);
                            }
                            if (exceptionData.timeStart && exceptionData.timeStart !== currentEntryData.timeStart) {
                                const newTime = exceptionData.timeStart && exceptionData.timeEnd ? 
                                    `${exceptionData.timeStart}-${exceptionData.timeEnd}` : 
                                    exceptionData.timeStart;
                                const originalTime = currentEntryData.timeStart && currentEntryData.timeEnd ? 
                                    `${currentEntryData.timeStart}-${currentEntryData.timeEnd}` : 
                                    currentEntryData.timeStart;
                                exceptionChanges.push(`time changed from ${originalTime} to ${newTime}`);
                            }
                            if (exceptionData.teacher && exceptionData.teacher !== currentEntryData.teacher) {
                                exceptionChanges.push(`teacher changed from ${currentEntryData.teacher} to ${exceptionData.teacher}`);
                            }
                            
                            if (exceptionChanges.length > 0) {
                                changes.push(`for week ${currentException.week}: ${exceptionChanges.join(', ')}`);
                            } else {
                                changes.push(`modified for week ${currentException.week}`);
                            }
                        }
                    } else {
                        // Modified exception - compare against previous exception
                        const prevChanges = baselineException.changes || {};
                        const currChanges = currentException.changes || {};
                        const exceptionChanges = [];
                        
                        // Compare exception fields
                        ['day', 'room', 'timeStart', 'timeEnd', 'teacher', 'cancelled'].forEach(field => {
                            const prevValue = prevChanges[field];
                            const currValue = currChanges[field];
                            
                            if (prevValue !== currValue) {
                                if (field === 'cancelled') {
                                    if (currValue && !prevValue) {
                                        exceptionChanges.push('cancelled');
                                    } else if (!currValue && prevValue) {
                                        exceptionChanges.push('un-cancelled');
                                    }
                                } else if (field === 'timeStart' && currChanges.timeEnd) {
                                    const newTime = `${currValue}-${currChanges.timeEnd}`;
                                    const oldTime = prevChanges.timeEnd ? `${prevValue}-${prevChanges.timeEnd}` : prevValue;
                                    exceptionChanges.push(`time: '${oldTime}' -> '${newTime}'`);
                                } else {
                                    exceptionChanges.push(`${field}: '${prevValue || 'N/A'}' -> '${currValue || 'N/A'}'`);
                                }
                            }
                        });
                        
                        if (exceptionChanges.length > 0) {
                            changes.push(`exception for week ${currentException.week} updated: ${exceptionChanges.join(', ')}`);
                        }
                    }
                });

                // Check for removed exceptions
                baselineExceptions.forEach(baselineException => {
                    const key = `${baselineException.week}-${baselineException.year}`;
                    if (!currentExceptionsMap.has(key)) {
                        changes.push(`exception for week ${baselineException.week} removed`);
                    }
                });

                if (changes.length > 0) {
                    netChangeDebugSummary.modified.push({
                        id: baselineEntry.id,
                        displayName: baselineEntry.displayName || currentEntryData.displayName || 'Unknown',
                        changeCount: changes.length
                    });
                    netChanges.push({
                        timestamp,
                        user: currentUser,
                        action: 'modify',
                        itemId: baselineEntry.id,
                        details: `'${baselineEntry.displayName || currentEntryData.displayName || 'Unknown'}' modified. Changes: ${changes.join(', ')}`
                    });
                } else {
                    // Only log if there were conflict differences but no field changes
                    if (baselineHasConflicts !== currentHasConflicts) {
                        netChangeDebugSummary.conflictOnly.push({
                            id: baselineEntry.id,
                            displayName: baselineEntry.displayName || currentEntryData.displayName || 'Unknown'
                        });
                    }
                }
            } else {
                netChangeDebugSummary.deleted.push({
                    id: baselineEntry.id,
                    displayName: baselineEntry.displayName || 'Unknown',
                    day: baselineEntry.day
                });
                netChanges.push({
                    timestamp,
                    user: currentUser,
                    action: 'delete',
                    itemId: baselineEntry.id,
                    details: `'${baselineEntry.displayName}' deleted from ${normalizeRoom(baselineEntry.room)} (${baselineEntry.day} ${baselineEntry.formattedTime}).`
                });
            }
        });

        filteredCurrent.forEach(currentEntry => {
            if (!baselineItemsMap.has(currentEntry.id)) {
                // Convert current entry to plain object for display
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function' 
                    ? currentEntry.toObject() 
                    : currentEntry;

                netChangeDebugSummary.created.push({
                    id: currentEntry.id,
                    displayName: currentEntry.displayName || currentEntryData.displayName || 'Unknown',
                    day: currentEntryData.day
                });

                netChanges.push({
                    timestamp,
                    user: currentUser,
                    action: 'create',
                    itemId: currentEntry.id,
                    details: `'${currentEntry.displayName || currentEntryData.displayName || 'Unknown'}' created in ${normalizeRoom(currentEntryData.room)} (${currentEntryData.day} ${currentEntryData.formattedTime}).`
                });
            }
        });

        console.log("[NetChanges] Summary:", {
            createdCount: netChangeDebugSummary.created.length,
            modifiedCount: netChangeDebugSummary.modified.length,
            deletedCount: netChangeDebugSummary.deleted.length,
            conflictOnlyCount: netChangeDebugSummary.conflictOnly.length,
            fieldDiffCount: netChangeDebugSummary.fieldDiffCount,
            conflictStateDiffCount: netChangeDebugSummary.conflictStateDiffCount,
            createdSample: netChangeDebugSummary.created.slice(0, 5),
            modifiedSample: netChangeDebugSummary.modified.slice(0, 5),
            deletedSample: netChangeDebugSummary.deleted.slice(0, 5),
            conflictOnlySample: netChangeDebugSummary.conflictOnly.slice(0, 5),
        });

        logger.debug("[useChangeTracking] NetChanges Summary:", {
            totalNetChanges: netChanges.length,
            changedItems: netChanges.map(change => ({
                id: change.itemId,
                action: change.action,
                details: change.details
            }))
        });
        setChangeLog(netChanges);
    }, [serverBaseline, user]);

    // Modal handlers
    const openChangeLogModal = () => {
        console.debug('[useChangeTracking] openChangeLogModal');
        setIsChangeLogModalOpen(true);
    };
    const closeChangeLogModal = () => {
        console.debug('[useChangeTracking] closeChangeLogModal');
        setIsChangeLogModalOpen(false);
    };

    // Pure helper to compute net changes (create/modify/delete) without mutating internal state
    const getNetChanges = useCallback((currentScheduleItems) => {
        if (!serverBaseline || !currentScheduleItems) return [];

        // Ignore placeholder entries in both baseline and current comparisons
        const filteredBaseline = (serverBaseline || []).filter(entry => !isPlaceholderEntry(entry));
        const filteredCurrent = (currentScheduleItems || []).filter(entry => !isPlaceholderEntry(entry));

        const netChanges = [];
        const baselineItemsMap = new Map(filteredBaseline.map(entry => [entry.id, entry]));
        const currentItemsMap = new Map(filteredCurrent.map(entry => [entry.id, entry]));
        const timestamp = new Date().toISOString();
        const currentUser = user?.username || 'Unknown';

        const normalizeTeacher = (val, entryObj) => {
            const formatted = typeof (entryObj && entryObj.teachersFormatted) === 'string' ? entryObj.teachersFormatted.trim() : '';
            if (formatted) return formatted;
            if (!val) return 'N/A';
            if (typeof val === 'string') return val;
            if (typeof val === 'object') return val.name || 'N/A';
            return String(val);
        };
        const normalizeRoom = (val) => {
            if (!val) return 'N/A';
            if (typeof val === 'string') return val;
            if (typeof val === 'object') return val.name || val.room_code || 'N/A';
            return String(val);
        };
        const normalizeTime = (val) => {
            if (!val || typeof val !== 'string') return 'N/A';
            const parts = val.split(':');
            if (parts.length >= 2) {
                const hh = parts[0].padStart(2, '0');
                const mm = parts[1].padStart(2, '0');
                return `${hh}:${mm}`;
            }
            return val;
        };
        const normalizeGroups = (val) => {
            if (!Array.isArray(val)) return 'N/A';
            return val.map(g => {
                if (typeof g === 'object' && g !== null) return g.name || g.group_code || g.id;
                return g;
            }).join(', ');
        };

        filteredBaseline.forEach(baselineEntry => {
            const currentEntry = currentItemsMap.get(baselineEntry.id);
            if (currentEntry) {
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function'
                    ? currentEntry.toObject()
                    : currentEntry;

                const changes = [];
                const fieldsToCompare = [
                    { key: 'day', label: 'day', getValue: (entry) => entry.day || 'N/A' },
                    { key: 'timeStart', label: 'timeStart', getValue: (entry) => normalizeTime(entry.timeStart) },
                    { key: 'timeEnd', label: 'timeEnd', getValue: (entry) => normalizeTime(entry.timeEnd) },
                    { key: 'course', label: 'course', getValue: (entry) => entry.course || 'N/A' },
                    { key: 'teachers', label: 'teachers', getValue: (entry) => normalizeTeacher(entry.teacher, entry) },
                    { key: 'groups', label: 'groups', getValue: (entry) => normalizeGroups(entry.groups) },
                    { key: 'room', label: 'room', getValue: (entry) => normalizeRoom(entry.room) },
                    { key: 'type', label: 'type', getValue: (entry) => entry.type || 'N/A' },
                    { key: 'weekSeries', label: 'weekSeries', getValue: (entry) => entry.weekSeries || entry.week_series || 'N/A' }
                ];

                fieldsToCompare.forEach(field => {
                    const baselineValue = field.getValue(baselineEntry);
                    const currentValue = field.getValue(currentEntryData);
                    if (baselineValue !== currentValue) {
                        changes.push(`${field.label}: '${baselineValue}' -> '${currentValue}'`);
                    }
                });

                const baselineExceptions = Array.isArray(baselineEntry.exceptions) ? baselineEntry.exceptions : [];
                const currentExceptions = Array.isArray(currentEntryData.exceptions) ? currentEntryData.exceptions : [];
                const baselineExceptionsMap = new Map(baselineExceptions.map(ex => [`${ex.week}-${ex.year}`, ex]));
                const currentExceptionsMap = new Map(currentExceptions.map(ex => [`${ex.week}-${ex.year}`, ex]));

                currentExceptions.forEach(currentException => {
                    const key = `${currentException.week}-${currentException.year}`;
                    const baselineException = baselineExceptionsMap.get(key);
                    if (!baselineException) {
                        const exceptionChanges = [];
                        const exceptionData = currentException.changes || {};
                        if (exceptionData.cancelled) {
                            changes.push(`cancelled for week ${currentException.week}`);
                        } else {
                            if (exceptionData.day && exceptionData.day !== currentEntryData.day) exceptionChanges.push(`moved from ${currentEntryData.day} to ${exceptionData.day}`);
                            if (exceptionData.room && exceptionData.room !== currentEntryData.room) exceptionChanges.push(`moved from ${currentEntryData.room} to ${exceptionData.room}`);
                            if (exceptionData.timeStart && exceptionData.timeStart !== currentEntryData.timeStart) {
                                const newTime = exceptionData.timeStart && exceptionData.timeEnd ? `${exceptionData.timeStart}-${exceptionData.timeEnd}` : exceptionData.timeStart;
                                const originalTime = currentEntryData.timeStart && currentEntryData.timeEnd ? `${currentEntryData.timeStart}-${currentEntryData.timeEnd}` : currentEntryData.timeStart;
                                exceptionChanges.push(`time changed from ${originalTime} to ${newTime}`);
                            }
                            if (exceptionData.teacher && exceptionData.teacher !== currentEntryData.teacher) exceptionChanges.push(`teacher changed from ${currentEntryData.teacher} to ${exceptionData.teacher}`);
                            if (exceptionChanges.length > 0) changes.push(`for week ${currentException.week}: ${exceptionChanges.join(', ')}`); else changes.push(`modified for week ${currentException.week}`);
                        }
                    } else {
                        const prevChanges = baselineException.changes || {};
                        const currChanges = currentException.changes || {};
                        const exceptionChanges = [];
                        ['day', 'room', 'timeStart', 'timeEnd', 'teacher', 'cancelled'].forEach(field => {
                            const prevValue = prevChanges[field];
                            const currValue = currChanges[field];
                            if (prevValue !== currValue) {
                                if (field === 'cancelled') {
                                    if (currValue && !prevValue) exceptionChanges.push('cancelled');
                                    else if (!currValue && prevValue) exceptionChanges.push('un-cancelled');
                                } else if (field === 'timeStart' && currChanges.timeEnd) {
                                    const newTime = `${currValue}-${currChanges.timeEnd}`;
                                    const oldTime = prevChanges.timeEnd ? `${prevValue}-${prevChanges.timeEnd}` : prevValue;
                                    exceptionChanges.push(`time: '${oldTime}' -> '${newTime}'`);
                                } else {
                                    exceptionChanges.push(`${field}: '${prevValue || 'N/A'}' -> '${currValue || 'N/A'}'`);
                                }
                            }
                        });
                        if (exceptionChanges.length > 0) changes.push(`exception for week ${currentException.week} updated: ${exceptionChanges.join(', ')}`);
                    }
                });

                baselineExceptions.forEach(baselineException => {
                    const key = `${baselineException.week}-${baselineException.year}`;
                    if (!currentExceptionsMap.has(key)) changes.push(`exception for week ${baselineException.week} removed`);
                });

                if (changes.length > 0) {
                    netChanges.push({
                        timestamp,
                        user: currentUser,
                        action: 'modify',
                        itemId: baselineEntry.id,
                        details: `'${baselineEntry.displayName || currentEntryData.displayName || 'Unknown'}' modified. Changes: ${changes.join(', ')}`
                    });
                }
            } else {
                netChanges.push({
                    timestamp,
                    user: currentUser,
                    action: 'delete',
                    itemId: baselineEntry.id,
                    details: `'${baselineEntry.displayName}' deleted from ${normalizeRoom(baselineEntry.room)} (${baselineEntry.day} ${baselineEntry.formattedTime}).`
                });
            }
        });

        filteredCurrent.forEach(currentEntry => {
            if (!baselineItemsMap.has(currentEntry.id)) {
                const currentEntryData = currentEntry && typeof currentEntry.toObject === 'function'
                    ? currentEntry.toObject()
                    : currentEntry;
                netChanges.push({
                    timestamp,
                    user: currentUser,
                    action: 'create',
                    itemId: currentEntry.id,
                    details: `'${currentEntry.displayName || currentEntryData.displayName || 'Unknown'}' created in ${normalizeRoom(currentEntryData.room)} (${currentEntryData.day} ${currentEntryData.formattedTime}).`
                });
            }
        });

        return netChanges;
    }, [serverBaseline, user]);

    // Set server baseline when data is fetched from server
    const updateServerBaseline = useCallback((scheduleItems) => {
        console.debug("[useChangeTracking] Baseline set", scheduleItems?.length || 0, "items");
        
        // Log warning for empty data to help debug
        if (!scheduleItems || scheduleItems.length === 0) {
            console.warn("[useChangeTracking] WARNING: setting baseline with empty data - may cause false change detection");
        }
        
        // Create a deep copy to avoid reference issues
        const baselineCopy = (scheduleItems || [])
            // Drop placeholder entries from baseline entirely
            .filter(item => !isPlaceholderEntry(item))
            .map(item => {
            // If it's a ScheduleEntry instance, use its data
            if (item && typeof item.toObject === 'function') {
                return item.toObject();
            }
            // Otherwise, create a copy of the plain object
            return { ...item };
        });
        
        // Log a sample of the baseline for debugging
        if (baselineCopy.length > 0) {
            console.debug("[useChangeTracking] Baseline sample entry:", {
                id: baselineCopy[0].id,
                displayName: baselineCopy[0].displayName,
                hasConflicts: baselineCopy[0].conflictsByWeek ? 'YES' : 'NO',
                conflictKeys: baselineCopy[0].conflictsByWeek ? Object.keys(baselineCopy[0].conflictsByWeek) : []
            });
        }
        
        setServerBaseline(baselineCopy);
        setChangeLog([]);
        // Reset tracked change IDs when establishing a new baseline
        try { 
            resetChangeIdSets(); 
            console.debug('[useChangeTracking] Change ID sets reset on baseline update');
        } catch (e) { /* noop */ }
    }, []);

    // Optimistically merge saved items into the current baseline by id
    const mergeIntoBaseline = useCallback((itemsToMerge) => {
        if (!Array.isArray(itemsToMerge) || itemsToMerge.length === 0) return;

        setServerBaseline(prevBaseline => {
            const baselineMap = new Map((prevBaseline || []).map(entry => [String(entry.id), entry]));

            itemsToMerge.forEach(item => {
                if (!item) return;
                if (isPlaceholderEntry(item)) return;
                const itemData = typeof item.toObject === 'function' ? item.toObject() : { ...item };
                const idKey = String(itemData.id);
                if (!idKey) return;
                baselineMap.set(idKey, itemData);
            });

            return Array.from(baselineMap.values());
        });
    }, []);

    // Ref to access merge function inside promise chains
    const mergeIntoBaselineRef = { current: mergeIntoBaseline };

    // Single point of baseline truth: set baseline only here when ready, once per version
    useEffect(() => {
        const ready = !!baselineReady && Array.isArray(baselineSource) && baselineSource.length > 0;
        const versionKey = baselineVersion || 'default';

        if (!ready) return;
        if (currentBaselineVersion === versionKey && serverBaseline && serverBaseline.length > 0) return;

        // Only set baseline after filtering placeholders and cloning via updateServerBaseline
        updateServerBaseline(baselineSource);
        setCurrentBaselineVersion(versionKey);
        console.debug('[useChangeTracking] Baseline established for version', versionKey, {
            baselineLen: baselineSource.length
        });
    }, [baselineReady, baselineSource, baselineVersion, currentBaselineVersion, serverBaseline, updateServerBaseline]);

    // Legacy method names for compatibility (just aliases)
    const initializeChangeTracking = updateServerBaseline;
    const setInitialState = updateServerBaseline;

    // Undo/Redo functions
    const performUndo = useCallback((currentScheduledInstances, setScheduledInstances) => {
        return undoRedoManager.undo(currentScheduledInstances, setScheduledInstances, addLogEntry);
    }, [undoRedoManager, addLogEntry]);

    const performRedo = useCallback((currentScheduledInstances, setScheduledInstances) => {
        return undoRedoManager.redo(currentScheduledInstances, setScheduledInstances, addLogEntry);
    }, [undoRedoManager, addLogEntry]);

    const canUndo = useCallback(() => {
        return undoRedoManager.canUndo();
    }, [undoRedoManager]);

    const canRedo = useCallback(() => {
        return undoRedoManager.canRedo();
    }, [undoRedoManager]);

    const getUndoRedoState = useCallback(() => {
        return {
            canUndo: undoRedoManager.canUndo(),
            canRedo: undoRedoManager.canRedo(),
            stackSizes: undoRedoManager.getStackSizes()
        };
    }, [undoRedoManager]);

    return {
        changeLog,
        addLogEntry,
        calculateNetChanges,
        initializeChangeTracking,
        setInitialState,
        isChangeLogModalOpen,
        openChangeLogModal,
        closeChangeLogModal,
        getNetChanges,
        serverBaseline,
        mergeIntoBaseline,
        // New: expose change ID tracking helpers
        getChangeIdSets,
        resetChangeIdSets,
        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    };
} 